import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Sprite } from '@pixi/react';
import gsap from 'gsap';
import * as PIXI from 'pixi.js';

import { jackPotStore } from '../../../../../store/jackPotStore';

import { Spine } from '../../../../../core/classes/spine';
import { isMobile } from '../../../../../core/helpers/isMobile';
import { getGameImages } from '../../../../../core/helpers/getGameImages';

import JackPotSceneItemGraphics from '../jackPotSceneItemGraphic';
import { userInfoStore } from '../../../../../store/userInfoStore';

const JackPotSceneItemContent = observer(
  ({
    x,
    y,
    winCard,
    setWinCard,
    itemIndex,
    jackpotIcons,
    jackPotItemEnum,
    jackpotSceneItemInfo,
    symbolMoveContainerRef,
  }) => {
    const {
      isExpiredJackpotCards,
      isAllowDoJackpotAction,
      openedJackpotCardCount,
      setOpenedJackpotCardCount,
      setIsAllowDoJackpotAction,
      setIsWinMoveAndScaleSymbolComplete,
    } = jackPotStore;
    const [spineBox, setSpineBox] = useState(null);
    const [spineSymbol, setSpineSymbol] = useState(null);
    const [sceneSymbolForWin, setSceneSymbolForWin] = useState(null);

    const jackPotSceneItemBoxRef = useRef(null);
    const jackPotSceneItemSymbolRef = useRef(null);
    const jackPotSceneItemWinSymbolRef = useRef(null);

    const { clubsImage, dimondImage, heartImage, spadesImage } = useMemo(() => {
      return getGameImages();
    }, []);

    const [image, setImage] = useState(clubsImage);
    const [imageVisible, setImageVisible] = useState(false);

    const { isWin, jackpotCard, index, isEnd } = useMemo(() => {
      return jackpotSceneItemInfo || {};
    }, [jackpotSceneItemInfo]);

    const symbolImages = useMemo(() => {
      return [clubsImage, dimondImage, heartImage, spadesImage];
    }, [clubsImage, dimondImage, heartImage, spadesImage]);

    const isOpened = useMemo(() => {
      return index === itemIndex;
    }, [index, itemIndex]);

    const skinName = useMemo(() => {
      return jackpotIcons[jackpotCard] || '';
    }, [jackpotCard, jackpotIcons]);

    useEffect(() => {
      const itemSymbol = new Spine({ key: 'jackPotSymbol' });
      const itemSymbolForWin = new Spine({ key: 'jackPotSymbol' });
      itemSymbol.play('idle', true);
      const itemBox = new Spine({ key: 'jackPotItem' });
      itemBox.isMoved = false;
      itemSymbol.isMoved = false;
      jackPotSceneItemWinSymbolRef.current.addChild(itemSymbolForWin);
      jackPotSceneItemSymbolRef.current.addChild(itemSymbol);
      jackPotSceneItemBoxRef.current.addChild(itemBox);
      setSpineBox(itemBox);
      setSpineSymbol(itemSymbol);
      setSceneSymbolForWin(itemSymbolForWin);
    }, []);

    const handleOpenedIconMoveAndScaleAnimation = useCallback(
      (sprite, count) => {
        if (isExpiredJackpotCards) {
          setImageVisible(false);
          return;
        }
        const container = new PIXI.Container();
        container.addChild(spineSymbol);
        container.x = x;
        container.y = y;
        symbolMoveContainerRef.current.addChild(container);
        spineSymbol.play('idle');

        jackPotItemEnum.forEach((item) => {
          const { id, positions } = item;
          if (id === jackpotCard && !sprite.isMoved) {
            setIsAllowDoJackpotAction(true);
            setImage(symbolImages[jackpotCard - 1]);
            setImageVisible(true);
            const scale = isMobile()
              ? Math.min(window.innerWidth / 540, window.innerHeight / 960)
              : 1;
            const marginX = isMobile() ? symbolMoveContainerRef.current.parent.parent.x / scale : 0;
            const marginY = isMobile() ? symbolMoveContainerRef.current.parent.parent.y / scale : 0;
            gsap.to(sprite, {
              x: positions[count].x - sprite.getGlobalPosition().x / scale + marginX,
              y: positions[count].y - sprite.getGlobalPosition().y / scale + marginY,
              duration: isEnd ? 0 : 0.7,
              onComplete: () => {
                sprite.play('win', false, isEnd ? 1000 : 1);
                sprite.addListener(
                  'complete',
                  () => {
                    if (isWin) {
                      setWinCard(jackpotCard);
                      setIsWinMoveAndScaleSymbolComplete(true);
                    }
                  },
                  null,
                  'win'
                );
              },
            });
            gsap.to(sprite.scale, {
              x: 1,
              y: 1,
              duration: isEnd ? 0 : 0.3,
              onComplete: () => {
                gsap.to(sprite.scale, {
                  x: isMobile() ? (count === 2 ? 0.4 : 0.25) : count === 2 ? 0.8 : 0.6,
                  y: isMobile() ? (count === 2 ? 0.4 : 0.25) : count === 2 ? 0.8 : 0.6,
                  duration: 0.3,
                });
              },
            });
          }
        });
      },
      [
        x,
        y,
        isEnd,
        isWin,
        setWinCard,
        spineSymbol,
        jackpotCard,
        symbolImages,
        jackPotItemEnum,
        isExpiredJackpotCards,
        symbolMoveContainerRef,
        setIsAllowDoJackpotAction,
        setIsWinMoveAndScaleSymbolComplete,
      ]
    );

    useEffect(() => {
      if ((isOpened || userInfoStore.isReplay) && skinName && spineBox) {
        !spineBox.isMoved && spineBox.play('open', false, isEnd ? 10000 : 1);
        const count = openedJackpotCardCount[jackpotCard];
        setOpenedJackpotCardCount(jackpotCard);
        setTimeout(() => {
          if (winCard === jackpotCard) {
            sceneSymbolForWin.skeleton.setSkinByName(skinName);
            sceneSymbolForWin.play('win', true);
            return;
          }
          !spineSymbol.isMoved && spineSymbol.play('win', false, isEnd ? 10000 : 1);
          spineSymbol.skeleton.setSkinByName(skinName);
          spineSymbol.addListener(
            'complete',
            () => {
              handleOpenedIconMoveAndScaleAnimation(spineSymbol, count);
              spineBox.isMoved = true;
              spineSymbol.isMoved = true;
            },
            null,
            'win'
          );
        }, 250);
      }
    }, [
      isEnd,
      isOpened,
      spineBox,
      winCard,
      skinName,
      spineSymbol,
      jackpotCard,
      symbolImages,
      sceneSymbolForWin,
      openedJackpotCardCount,
      symbolMoveContainerRef,
      setOpenedJackpotCardCount,
      handleOpenedIconMoveAndScaleAnimation,
    ]);

    return (
      <Container x={x} y={y} key={itemIndex} value={itemIndex} name='jackPotItemSectionContainer'>
        <Sprite texture={image} scale={0.5} anchor={0.5} visible={imageVisible} />
        <Container ref={jackPotSceneItemBoxRef} name='JackPotSceneItemBoxContainer' />
        <Container ref={jackPotSceneItemWinSymbolRef} name='JackPotSceneItemWinSymbolContainer' />
        <Container ref={jackPotSceneItemSymbolRef} name='JackPotSceneItemSymbolContainer' />
        <JackPotSceneItemGraphics
          width={135}
          height={137}
          index={itemIndex}
          isOpened={isOpened}
          jackPotSceneItemBoxRef={jackPotSceneItemBoxRef}
          isAllowDoJackpotAction={isAllowDoJackpotAction}
          setIsAllowDoJackpotAction={setIsAllowDoJackpotAction}
        />
      </Container>
    );
  }
);

export default JackPotSceneItemContent;
